import React, { useEffect, useState, useRef } from 'react';
import { BrainIcon, ImageIcon, SendIcon, MessageSquareIcon, ReplyIcon } from 'lucide-react';
interface Post {
  id: number;
  author: {
    name: string;
    avatar: string;
    role: string;
  };
  content: string;
  timestamp: string;
  likes: number;
  comments: Comment[];
  hasAlgaTag: boolean;
}
interface Comment {
  id: number;
  author: {
    name: string;
    avatar: string;
    isAlga?: boolean;
  };
  content: string;
  timestamp: string;
  replies?: Reply[];
}
interface Reply {
  id: number;
  author: {
    name: string;
    avatar: string;
    isAlga?: boolean;
  };
  content: string;
  timestamp: string;
}
const SAMPLE_POSTS: Post[] = [{
  id: 1,
  author: {
    name: 'Marco Rossi',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    role: 'Data Scientist @ Intel'
  },
  content: 'Interessante articolo sulla circular economy nel settore tech. @ALGA quali sono le tue considerazioni sui punti discussi?',
  timestamp: '2 ore fa',
  likes: 12,
  hasAlgaTag: true,
  comments: [{
    id: 1,
    author: {
      name: 'ALGA',
      avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png',
      isAlga: true
    },
    content: "Grazie per avermi taggato! L'economia circolare nel settore tech è un tema cruciale. I principali punti da considerare sono:\n\n1. Design sostenibile dei prodotti\n2. Gestione del ciclo di vita\n3. Recupero e riutilizzo dei materiali\n\nSarei felice di approfondire questi aspetti in una discussione dedicata.",
    timestamp: '2 ore fa',
    replies: []
  }]
}];
const GroupFeed: React.FC<{
  groupId?: string;
}> = ({
  groupId
}) => {
  const [posts, setPosts] = useState<Post[]>(SAMPLE_POSTS);
  const [newPostContent, setNewPostContent] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Handle @ mentions in the textarea
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setNewPostContent(text);
    // Check if the last character typed is @
    const lastChar = text.slice(-1);
    if (lastChar === '@') {
      setShowMentionDropdown(true);
    } else if (text.indexOf('@') === -1) {
      setShowMentionDropdown(false);
    }
  };
  const insertMention = () => {
    const cursorPosition = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = newPostContent.substring(0, cursorPosition);
    const textAfterCursor = newPostContent.substring(cursorPosition);
    // Find the last @ symbol before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      // Replace the @ with @ALGA
      const newText = textBeforeCursor.substring(0, lastAtIndex) + '@ALGA ' + textAfterCursor;
      setNewPostContent(newText);
    }
    setShowMentionDropdown(false);
    textareaRef.current?.focus();
  };
  const handleCreatePost = () => {
    if (!newPostContent.trim()) return;
    const hasAlgaTag = newPostContent.toLowerCase().includes('@alga');
    const newPost: Post = {
      id: posts.length + 1,
      author: {
        name: 'Edoardo Gatner',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg',
        role: 'CEO'
      },
      content: newPostContent,
      timestamp: 'Ora',
      likes: 0,
      comments: [],
      hasAlgaTag
    };
    // Add the post first
    setPosts([newPost, ...posts]);
    setNewPostContent('');
    // Immediately add ALGA's response if tagged
    if (hasAlgaTag) {
      setTimeout(() => {
        setPosts(prevPosts => {
          const updatedPosts = [...prevPosts];
          const postIndex = updatedPosts.findIndex(p => p.id === newPost.id);
          if (postIndex !== -1) {
            updatedPosts[postIndex] = {
              ...updatedPosts[postIndex],
              comments: [...updatedPosts[postIndex].comments, {
                id: Date.now(),
                author: {
                  name: 'ALGA',
                  avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png',
                  isAlga: true
                },
                content: "Grazie per avermi taggato! Ho analizzato il tuo post e sono qui per aiutarti. Possiamo approfondire l'argomento insieme?",
                timestamp: 'Ora',
                replies: []
              }]
            };
          }
          return updatedPosts;
        });
      }, 500); // Small delay to make it feel natural
    }
  };
  return <div className="space-y-6" data-id="element-1144">
      {/* Create Post */}
      <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1145">
        <div className="flex items-start gap-4" data-id="element-1146">
          <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" data-id="element-1147">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg" alt="User Avatar" className="w-full h-full object-cover" data-id="element-1148" />
          </div>
          <div className="flex-1 relative" data-id="element-1149">
            <textarea ref={textareaRef} value={newPostContent} onChange={handleTextChange} placeholder="Condividi qualcosa con il gruppo..." className="w-full min-h-[100px] p-3 rounded-lg border border-gray-200 focus:outline-none focus:border-custom-green resize-none" data-id="element-1150" />
            {/* @ Mention Dropdown */}
            {showMentionDropdown && <div ref={dropdownRef} className="absolute z-10 bg-white rounded-lg shadow-lg border border-gray-200 w-64 mt-1" data-id="element-1151">
                <div className="p-3 flex items-center gap-2 hover:bg-gray-50 cursor-pointer" onClick={insertMention} data-id="element-1152">
                  <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-pink-500" data-id="element-1153">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-1154" />
                  </div>
                  <div data-id="element-1155">
                    <div className="font-medium text-gray-800" data-id="element-1156">ALGA</div>
                    <div className="text-xs text-gray-500" data-id="element-1157">AI Assistant</div>
                  </div>
                </div>
              </div>}
            <div className="flex justify-between items-center mt-3" data-id="element-1158">
              <div className="flex items-center gap-2 text-gray-500" data-id="element-1159">
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-1160">
                  <ImageIcon size={20} data-id="element-1161" />
                </button>
                <button className="p-2 hover:bg-gray-100 rounded-full transition-colors" onClick={() => setNewPostContent(prev => prev + ' @ALGA ')} data-id="element-1162">
                  <BrainIcon size={20} data-id="element-1163" />
                </button>
              </div>
              <button onClick={handleCreatePost} className="bg-custom-green text-white px-4 py-2 rounded-lg hover:bg-custom-green/90 transition-colors flex items-center gap-2" data-id="element-1164">
                <SendIcon size={16} data-id="element-1165" />
                Pubblica
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Posts List */}
      <div className="space-y-4" data-id="element-1166">
        {posts.map(post => <PostCard key={post.id} post={post} onUpdatePost={updatedPost => {
        setPosts(posts.map(p => p.id === updatedPost.id ? updatedPost : p));
      }} data-id="element-1167" />)}
      </div>
    </div>;
};
const PostCard: React.FC<{
  post: Post;
  onUpdatePost: (post: Post) => void;
}> = ({
  post,
  onUpdatePost
}) => {
  return <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1168">
      {/* Author Info */}
      <div className="flex items-center gap-3 mb-4" data-id="element-1169">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0" data-id="element-1170">
          <img src={post.author.avatar} alt={post.author.name} className="w-full h-full object-cover" data-id="element-1171" />
        </div>
        <div data-id="element-1172">
          <h3 className="font-medium text-gray-800" data-id="element-1173">{post.author.name}</h3>
          <p className="text-sm text-gray-500" data-id="element-1174">
            {post.author.role} • {post.timestamp}
          </p>
        </div>
      </div>
      {/* Post Content */}
      <p className="text-gray-700 mb-4 whitespace-pre-wrap" data-id="element-1175">{post.content}</p>
      {/* Actions */}
      <div className="flex items-center gap-4 text-gray-500 text-sm border-t border-gray-100 pt-3" data-id="element-1176">
        <button className="flex items-center gap-1" data-id="element-1177">
          <span data-id="element-1178">👍</span> {post.likes}
        </button>
        <button className="flex items-center gap-1" data-id="element-1179">
          <MessageSquareIcon size={16} data-id="element-1180" /> {post.comments.length}
        </button>
      </div>
      {/* Comments */}
      {post.comments.length > 0 && <div className="mt-4 space-y-3" data-id="element-1181">
          {post.comments.map(comment => <CommentCard key={comment.id} comment={comment} postId={post.id} onAddReply={(commentId, newReply) => {
        const updatedComments = post.comments.map(c => {
          if (c.id === commentId) {
            return {
              ...c,
              replies: [...(c.replies || []), newReply]
            };
          }
          return c;
        });
        onUpdatePost({
          ...post,
          comments: updatedComments
        });
      }} data-id="element-1182" />)}
        </div>}
    </div>;
};
const CommentCard: React.FC<{
  comment: Comment;
  postId: number;
  onAddReply: (commentId: number, reply: Reply) => void;
}> = ({
  comment,
  postId,
  onAddReply
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setReplyContent(text);
    // Check if the last character typed is @
    const lastChar = text.slice(-1);
    if (lastChar === '@') {
      setShowMentionDropdown(true);
    } else if (text.indexOf('@') === -1) {
      setShowMentionDropdown(false);
    }
  };
  const insertMention = () => {
    const cursorPosition = replyTextareaRef.current?.selectionStart || 0;
    const textBeforeCursor = replyContent.substring(0, cursorPosition);
    const textAfterCursor = replyContent.substring(cursorPosition);
    // Find the last @ symbol before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      // Replace the @ with @ALGA
      const newText = textBeforeCursor.substring(0, lastAtIndex) + '@ALGA ' + textAfterCursor;
      setReplyContent(newText);
    }
    setShowMentionDropdown(false);
    replyTextareaRef.current?.focus();
  };
  const handleSubmitReply = () => {
    if (!replyContent.trim()) return;
    const hasAlgaTag = replyContent.toLowerCase().includes('@alga');
    // Add user's reply first
    const newReply: Reply = {
      id: Date.now(),
      author: {
        name: 'Edoardo Gatner',
        avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/4hFhikAPJ46vhrcKePNA5J/IMG_0014.jpg'
      },
      content: replyContent,
      timestamp: 'Ora'
    };
    onAddReply(comment.id, newReply);
    setReplyContent('');
    setIsReplying(false);
    // Immediately add ALGA's response if tagged with a small delay to make it feel natural
    if (hasAlgaTag) {
      setTimeout(() => {
        const algaReply: Reply = {
          id: Date.now() + 1,
          author: {
            name: 'ALGA',
            avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png',
            isAlga: true
          },
          content: 'Grazie per avermi taggato nella risposta! Sono qui per aiutarti con qualsiasi domanda o approfondimento.',
          timestamp: 'Ora'
        };
        onAddReply(comment.id, algaReply);
      }, 500);
    }
  };
  return <div className="space-y-3" data-id="element-1183">
      <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-3" data-id="element-1184">
        <div className={`w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ${comment.author.isAlga ? 'border-2 border-pink-500' : ''}`} data-id="element-1185">
          <img src={comment.author.avatar} alt={comment.author.name} className="w-full h-full object-cover" data-id="element-1186" />
        </div>
        <div className="flex-1" data-id="element-1187">
          <div className="flex items-center gap-2" data-id="element-1188">
            <h4 className="font-medium text-gray-800" data-id="element-1189">{comment.author.name}</h4>
            {comment.author.isAlga && <span className="bg-custom-green/10 text-custom-green text-xs px-2 py-0.5 rounded-full" data-id="element-1190">
                AI Assistant
              </span>}
            <span className="text-sm text-gray-500" data-id="element-1191">{comment.timestamp}</span>
          </div>
          <p className="text-gray-700 text-sm mt-1 whitespace-pre-wrap" data-id="element-1192">
            {comment.content}
          </p>
          <div className="mt-2" data-id="element-1193">
            <button onClick={() => setIsReplying(!isReplying)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1" data-id="element-1194">
              <ReplyIcon size={14} data-id="element-1195" />
              Rispondi
            </button>
          </div>
        </div>
      </div>
      {/* Replies */}
      {comment.replies && comment.replies.length > 0 && <div className="ml-8 space-y-3" data-id="element-1196">
          {comment.replies.map(reply => <div key={reply.id} className="flex items-start gap-3 bg-gray-50 rounded-lg p-3" data-id="element-1197">
              <div className={`w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ${reply.author.isAlga ? 'border-2 border-pink-500' : ''}`} data-id="element-1198">
                <img src={reply.author.avatar} alt={reply.author.name} className="w-full h-full object-cover" data-id="element-1199" />
              </div>
              <div data-id="element-1200">
                <div className="flex items-center gap-2" data-id="element-1201">
                  <h4 className="font-medium text-gray-800" data-id="element-1202">
                    {reply.author.name}
                  </h4>
                  {reply.author.isAlga && <span className="bg-custom-green/10 text-custom-green text-xs px-2 py-0.5 rounded-full" data-id="element-1203">
                      AI Assistant
                    </span>}
                  <span className="text-sm text-gray-500" data-id="element-1204">
                    {reply.timestamp}
                  </span>
                </div>
                <p className="text-gray-700 text-sm mt-1 whitespace-pre-wrap" data-id="element-1205">
                  {reply.content}
                </p>
              </div>
            </div>)}
        </div>}
      {/* Reply Form */}
      {isReplying && <div className="ml-8 bg-white rounded-lg border border-gray-200 p-3 relative" data-id="element-1206">
          <textarea ref={replyTextareaRef} value={replyContent} onChange={handleTextChange} placeholder="Scrivi una risposta..." className="w-full min-h-[80px] p-2 rounded-lg border border-gray-200 focus:outline-none focus:border-custom-green resize-none text-sm" data-id="element-1207" />
          {/* @ Mention Dropdown */}
          {showMentionDropdown && <div ref={dropdownRef} className="absolute z-10 bg-white rounded-lg shadow-lg border border-gray-200 w-64 mt-1" data-id="element-1208">
              <div className="p-3 flex items-center gap-2 hover:bg-gray-50 cursor-pointer" onClick={insertMention} data-id="element-1209">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-pink-500" data-id="element-1210">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/x28iigDaSByhSaf2N4iVm6/IMG_0007.png" alt="ALGA AI Assistant" className="w-full h-full object-cover" data-id="element-1211" />
                </div>
                <div data-id="element-1212">
                  <div className="font-medium text-gray-800" data-id="element-1213">ALGA</div>
                  <div className="text-xs text-gray-500" data-id="element-1214">AI Assistant</div>
                </div>
              </div>
            </div>}
          <div className="flex justify-between items-center mt-2" data-id="element-1215">
            <div className="flex items-center gap-2 text-gray-500" data-id="element-1216">
              <button className="p-1 hover:bg-gray-100 rounded-full transition-colors" onClick={() => setReplyContent(prev => prev + ' @ALGA ')} data-id="element-1217">
                <BrainIcon size={16} data-id="element-1218" />
              </button>
            </div>
            <div className="flex gap-2" data-id="element-1219">
              <button onClick={() => setIsReplying(false)} className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700" data-id="element-1220">
                Annulla
              </button>
              <button onClick={handleSubmitReply} className="bg-custom-green text-white px-3 py-1 rounded-lg hover:bg-custom-green/90 transition-colors text-sm flex items-center gap-1" data-id="element-1221">
                <SendIcon size={14} data-id="element-1222" />
                Invia
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default GroupFeed;